import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Store as StoreIcon, X, Printer } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// One row per physical store. `is_default` flags the flagship (DrinX
// Amman) so the sentinel UUID stays visible in the list; only super_admin
// + operations can edit rows. Cashiers get assigned to a store via
// `cashier_users.store_id` (default = flagship UUID) and the till
// resolves the store's receipt-template fields at sign-in.

interface Store {
  id: string
  name: string
  active: number
  is_default: number
  address: string | null
  phone: string | null
  tax_number: string | null
  receipt_tagline: string | null
  receipt_header_line: string | null
  receipt_footer_line: string | null
  receipt_promo_line: string | null
  created_at: string
}

export default function Stores() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()
  const [modal, setModal] = useState<Store | 'new' | null>(null)

  const { data, isLoading } = useQuery<{ stores: Store[] }>({
    queryKey: ['stores'],
    queryFn: () => api.get('/stores').then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['stores'] })

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <StoreIcon size={20} className="text-[#c41e3a]" />
            Stores
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Each store carries its own receipt template — the physical POS receipt reflects the store the cashier is assigned to, not the chain. Edit the tagline / header / footer / promo lines here; the till picks up changes on the cashier's next sign-in.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            New store
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.stores ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No stores yet. Add the flagship to start using the POS.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Address</th>
                <th className="text-left px-5 py-3 font-medium">Phone</th>
                <th className="text-left px-5 py-3 font-medium">Tax No.</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Created</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.stores ?? []).map(s => (
                <tr key={s.id} className="border-t border-white/10">
                  <td className="px-5 py-3 text-sm text-white font-medium">
                    <div className="flex items-center gap-2">
                      {s.name}
                      {s.is_default === 1 && (
                        <span className="text-[10px] uppercase tracking-wide bg-[#c41e3a]/15 text-[#c41e3a] px-2 py-0.5 rounded-full">Flagship</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-400 max-w-xs truncate">{s.address ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-gray-400 font-mono whitespace-nowrap">{s.phone ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-gray-400 font-mono">{s.tax_number ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3">
                    {s.active === 1 ? (
                      <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Open</span>
                    ) : (
                      <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Closed</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{formatDateTime(s.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    {canManage && (
                      <button
                        onClick={() => setModal(s)}
                        className="p-1.5 text-gray-500 hover:text-white"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <StoreModal
          store={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { refresh(); setModal(null) }}
        />
      )}
    </div>
  )
}

// ── Store editor ────────────────────────────────────────────────────
//
// Left column: the mutable form fields.
// Right column: a live thermal-receipt preview that re-renders on every
// keystroke so the operator can see how the printed receipt will look.
// The preview mimics the ESC/POS layout — logo header, tagline, address
// block, itemised sample, totals, JoFotara QR placeholder, legal footer.

function StoreModal({
  store,
  onClose,
  onSaved,
}: {
  store: Store | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = store === null
  const [name, setName] = useState(store?.name ?? '')
  const [active, setActive] = useState((store?.active ?? 1) === 1)
  const [address, setAddress] = useState(store?.address ?? '')
  const [phone, setPhone] = useState(store?.phone ?? '')
  const [taxNumber, setTaxNumber] = useState(store?.tax_number ?? '')
  const [tagline, setTagline] = useState(store?.receipt_tagline ?? '')
  const [headerLine, setHeaderLine] = useState(store?.receipt_header_line ?? '')
  const [footerLine, setFooterLine] = useState(store?.receipt_footer_line ?? '')
  const [promoLine, setPromoLine] = useState(store?.receipt_promo_line ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(() => ({
    name,
    address,
    phone,
    taxNumber,
    tagline,
    headerLine,
    footerLine,
    promoLine,
  }), [name, address, phone, taxNumber, tagline, headerLine, footerLine, promoLine])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) { setError('Name is required'); return }
    setSubmitting(true)
    try {
      const body = {
        name: name.trim(),
        active,
        address: address.trim(),
        phone: phone.trim(),
        tax_number: taxNumber.trim(),
        receipt_tagline: tagline.trim(),
        receipt_header_line: headerLine.trim(),
        receipt_footer_line: footerLine.trim(),
        receipt_promo_line: promoLine.trim(),
      }
      if (isNew) {
        await api.post('/stores', body)
      } else {
        await api.patch(`/stores/${store!.id}`, body)
      }
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Couldn\'t save.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-5xl bg-[#1a1a1a] border border-white/10 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="col-span-full flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">
            {isNew ? 'Add store' : `Edit ${store?.name}`}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* ── Form column ────────────────────────────────────────────── */}
        <div className="space-y-4">
          <Field label="Store name" hint="Shown at the top of the printed receipt (in bold under the wordmark).">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={inputCls}
              placeholder="e.g. DrinX · Wadi Saqra"
              autoFocus
            />
          </Field>

          <Field label="Address" hint="Printed in the legal footer.">
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              className={inputCls}
              placeholder="Haddad Plaza, 203 Arar Street, Wadi Saqra"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" hint="Legal footer.">
              <input
                type="text"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className={inputCls}
                placeholder="+962 79 110 6006"
              />
            </Field>
            <Field label="Tax number" hint="Falls back to the JoFotara TIN.">
              <input
                type="text"
                value={taxNumber}
                onChange={e => setTaxNumber(e.target.value)}
                className={`${inputCls} font-mono`}
                placeholder="17021855"
              />
            </Field>
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Receipt template</h4>
            <p className="text-xs text-gray-600 mb-3">
              Optional per-store text lines. Blank fields fall back to the built-in defaults (tagline "Sip, Savor & Celebrate", generic thank-you footer, no promo line).
            </p>
          </div>

          <Field label="Tagline" hint="Under the DrinX wordmark. Keep it short.">
            <input
              type="text"
              value={tagline}
              onChange={e => setTagline(e.target.value)}
              className={inputCls}
              placeholder="Sip, Savor & Celebrate"
              maxLength={48}
            />
          </Field>

          <Field label="Header line" hint="Under the address (e.g. opening hours).">
            <input
              type="text"
              value={headerLine}
              onChange={e => setHeaderLine(e.target.value)}
              className={inputCls}
              placeholder="Open Sun–Sat · 11am–11pm"
              maxLength={48}
            />
          </Field>

          <Field label="Footer line" hint="Replaces the default thank-you line.">
            <input
              type="text"
              value={footerLine}
              onChange={e => setFooterLine(e.target.value)}
              className={inputCls}
              placeholder="Thank you — see you soon!"
              maxLength={48}
            />
          </Field>

          <Field label="Promo line" hint="Bold line above the footer (clear when the promo ends).">
            <input
              type="text"
              value={promoLine}
              onChange={e => setPromoLine(e.target.value)}
              className={inputCls}
              placeholder="Follow @drinxjo for weekly drops"
              maxLength={48}
            />
          </Field>

          {!isNew && (
            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
              <input
                type="checkbox"
                id="store_active"
                checked={active}
                onChange={e => setActive(e.target.checked)}
                className="accent-[#c41e3a]"
              />
              <label htmlFor="store_active" className="text-xs text-gray-400">
                Open (uncheck to mark closed — hides from reporting selectors)
              </label>
            </div>
          )}

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
            >
              {submitting ? 'Saving…' : isNew ? 'Add store' : 'Save'}
            </button>
          </div>
        </div>

        {/* ── Live receipt preview ──────────────────────────────────── */}
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
            <Printer size={12} /> Receipt preview
          </div>
          <ReceiptPreview {...preview} />
          <p className="text-xs text-gray-600 mt-3 leading-relaxed">
            Approximates the 80mm thermal print — final render is slightly different because the printer uses a fixed monospace font. Changes to the DrinX wordmark, item layout, totals block, JoFotara QR block, and JoFotara tax number are template-fixed and can't be edited here.
          </p>
        </div>
      </form>
    </div>
  )
}

const inputCls =
  'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-600 mt-1">{hint}</p>}
    </div>
  )
}

// Approximates the ESC/POS layout in escpos_printer.dart so the operator
// can visualise what a real print looks like. Monospace font + narrow
// column = the "thermal roll" feel. Not pixel-perfect (fixed-width vs
// proportional at real 80mm dot count), but close enough for a template
// editor.
function ReceiptPreview(p: {
  name: string
  address: string
  phone: string
  taxNumber: string
  tagline: string
  headerLine: string
  footerLine: string
  promoLine: string
}) {
  const tagline = p.tagline.trim() || 'Sip, Savor & Celebrate'
  const footer = p.footerLine.trim() || 'Thank you for shopping with DrinX.'
  return (
    <div className="bg-white text-black rounded-md shadow-inner p-4 font-mono text-[11px] leading-snug max-w-[300px] mx-auto">
      <div className="text-center">
        <div className="text-2xl font-black tracking-tight" style={{ fontFamily: 'serif' }}>
          Drin<span className="text-[#c41e3a]">X</span>
        </div>
        <div className="italic text-[10px] mt-0.5">{tagline}</div>
        {p.name.trim() && <div className="font-bold mt-1">{p.name.trim()}</div>}
        {p.headerLine.trim() && <div className="mt-0.5">{p.headerLine.trim()}</div>}
      </div>
      <div className="border-t border-dashed border-gray-400 my-2" />
      <div className="grid grid-cols-2 text-[10px]">
        <div className="font-bold">Order</div><div className="text-right">POS-000123</div>
        <div className="font-bold">Date</div><div className="text-right">2026-07-01 14:32</div>
        <div className="font-bold">Cashier</div><div className="text-right">Sample Cashier</div>
        <div className="font-bold">Customer</div><div className="text-right">Walk-in</div>
      </div>
      <div className="border-t border-dashed border-gray-400 my-2" />
      <div>
        <div>1× Johnnie Walker Black 750ml</div>
        <div className="text-right">JD 32.500</div>
        <div>2× Corona Extra 355ml</div>
        <div className="text-right">JD 5.000</div>
      </div>
      <div className="border-t border-dashed border-gray-400 my-2" />
      <div className="grid grid-cols-2 text-[10px]">
        <div>Subtotal</div><div className="text-right">JD 37.500</div>
        <div className="font-bold pt-1">TOTAL</div><div className="text-right font-bold text-[13px]">JD 37.500</div>
      </div>
      <div className="mt-2 grid grid-cols-2 text-[10px]">
        <div>Payment</div><div className="text-right">CASH</div>
      </div>
      <div className="border-t border-dashed border-gray-400 my-2" />
      <div className="text-center text-[10px] font-bold">JOFOTARA E-INVOICE</div>
      <div className="my-1 mx-auto w-16 h-16 bg-gray-200 flex items-center justify-center text-[8px] text-gray-500">
        [ QR ]
      </div>
      <div className="text-center text-[9px]">Scan to verify with ISTD.</div>
      <div className="border-t border-dashed border-gray-400 my-2" />
      <div className="text-center">
        <div className="font-bold">Yousef M Haddad Company & Partners</div>
        {p.taxNumber.trim() && <div>Tax Reg. No.: {p.taxNumber.trim()}</div>}
        {p.address.trim() && <div>{p.address.trim()}</div>}
        {p.phone.trim() && <div>{p.phone.trim()}</div>}
        {p.promoLine.trim() && (
          <div className="mt-1 font-bold">{p.promoLine.trim()}</div>
        )}
        <div className="mt-1">{footer}</div>
      </div>
    </div>
  )
}
